import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { CandidateCard } from "@/components/CandidateCard";
import { api } from "@/lib/api";
import { Search } from "lucide-react";

export const CandidateFinder = ({
  title = "Find your candidate",
  subtitle = "Choose your municipality and election to see participating candidates. After you have voted, you can witness your vote on your candidate's page.",
  declareMode = false,
  extraTop = null,
}) => {
  const [munis, setMunis] = useState([]);
  const [elections, setElections] = useState([]);
  const [offices, setOffices] = useState([]);
  const [muniId, setMuniId] = useState("");
  const [electionId, setElectionId] = useState("");
  const [officeId, setOfficeId] = useState("all");
  const [candidates, setCandidates] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/public/municipalities").then((r) => setMunis(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!muniId) return;
    setElectionId("");
    setOfficeId("all");
    setCandidates(null);
    api.get("/public/elections", { params: { municipality_id: muniId } }).then((r) => {
      setElections(r.data);
      if (r.data.length === 1) setElectionId(r.data[0].id);
    });
  }, [muniId]);

  useEffect(() => {
    if (!electionId) return;
    api.get("/public/offices", { params: { election_id: electionId } }).then((r) => setOffices(r.data));
  }, [electionId]);

  useEffect(() => {
    if (!muniId || !electionId) return;
    setLoading(true);
    const election = elections.find((e) => e.id === electionId);
    const params = { municipality_id: muniId, election_year: election?.election_year };
    api
      .get("/public/candidates", { params })
      .then((r) => {
        let list = r.data;
        if (officeId !== "all") list = list.filter((c) => c.office.id === officeId);
        setCandidates(list);
      })
      .finally(() => setLoading(false));
  }, [muniId, electionId, officeId, elections]);

  return (
    <div className="wyv-container py-12 sm:py-16">
      <div className="max-w-2xl">
        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="find-candidate-title">
          {title}
        </h1>
        <p className="text-muted-foreground mt-3">{subtitle}</p>
      </div>
      {extraTop}

      <Card className="mt-8 max-w-3xl">
        <CardContent className="p-6">
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label className="mb-2 block">Municipality</Label>
              <Select value={muniId} onValueChange={setMuniId}>
                <SelectTrigger data-testid="find-municipality-select">
                  <SelectValue placeholder="Select municipality" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {munis.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-2 block">Election</Label>
              <Select value={electionId} onValueChange={setElectionId} disabled={!muniId}>
                <SelectTrigger data-testid="find-election-select">
                  <SelectValue placeholder="Select election" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {elections.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-2 block">Office / Ward</Label>
              <Select value={officeId} onValueChange={setOfficeId} disabled={!electionId}>
                <SelectTrigger data-testid="find-office-select">
                  <SelectValue placeholder="All offices" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All offices</SelectItem>
                  {offices.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="mt-8 max-w-4xl">
        {loading && (
          <div className="grid sm:grid-cols-2 gap-4">
            <Skeleton className="h-44" />
            <Skeleton className="h-44" />
          </div>
        )}
        {!loading && candidates && candidates.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <Search className="h-8 w-8 text-slate-300 mx-auto mb-3" />
              <p className="font-heading font-semibold text-foreground">No participating candidates yet</p>
              <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                No candidate for this selection has joined Witness Your Vote so far. Know a candidate who should?{" "}
                <a href="/invite-a-candidate" className="text-primary font-medium hover:underline">Invite them</a>.
              </p>
            </CardContent>
          </Card>
        )}
        {!loading && candidates && candidates.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="find-candidate-results">
            {candidates.map((c) => (
              <CandidateCard key={c.id} candidate={c} declareMode={declareMode} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default function FindCandidate() {
  return <CandidateFinder />;
}
